import classNames from 'classnames';
import { Button, FileInput, Label, TextInput } from 'flowbite-react';
import { get } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue, SingleValue } from 'react-select';
import { Dropdown, gradYearOptions, postgraduateDegrees, undergraduateDegrees } from 'shared-utils';
import CustomDropDown from 'src/components/shared/CustomDropDown';
import CollegeSelect from 'src/components/shared/CollegeSelect';
import { HiOutlineArrowRight } from 'react-icons/hi';
import { useToast } from 'src/context/ToastProvider';
import { useThemeContext } from 'src/context/ThemeProvider';
import {
	PostgraduateDegreeTypes,
	ProfileFormData,
	RegisterUserInput,
	ScoreType,
	SkillType,
	UndergraduateDegreeTypes,
	UserExperienceLevelType,
} from 'types';
import { getPolicyForS3NonVideoWithoutAuth, registerUser } from 'src/apis/user';
import { useUserContext } from 'src/context/UserProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import Container from '../shared/Container';
import { useGetAllPublicSkills } from 'src/hooks/skill.hooks';
import OfflineDriveDropdown from '../driveAdmin/offlineDriveCandidatesList/OfflineDriveDropdown';

type FormDataType = {
	name: RegisterUserInput['name'];
	email: RegisterUserInput['email'];
	phone: RegisterUserInput['phone'];
	experience?: string;
	companyName?: RegisterUserInput['companyName'];
	usedReferralCode?: boolean;
	experienceLevel?: UserExperienceLevelType;
	totalExperience?: string;
	techStacks?: string[];
	gender?: ProfileFormData['gender'];
	resume?: ProfileFormData['resume'];
	college?: ProfileFormData['college'];
	degree?: ProfileFormData['degree'];
	specialisation?: ProfileFormData['specialisation'];
	currentScore?: ProfileFormData['currentScore'];
	gradYear?: ProfileFormData['gradYear'];
	currentScoreType?: ProfileFormData['currentScoreType'];
	selectedDrive?: string;
};
type UserRegisteredFormDataType = {
	name: RegisterUserInput['name'];
	email: RegisterUserInput['email'];
	phone: RegisterUserInput['phone'];
	experience?: RegisterUserInput['experience'];
	companyName?: RegisterUserInput['companyName'];
	usedReferralCode?: boolean;
	experienceLevel?: UserExperienceLevelType;
	totalExperience?: string;
	isRecruiter?: boolean;
};
type AdditionalUserDriveRegisteredFormDataType = {
	techStacks?: string[];
	gender?: ProfileFormData['gender'];
	resume?: ProfileFormData['resume'];
	college?: ProfileFormData['college'];
	degree?: ProfileFormData['degree'];
	specialisation?: ProfileFormData['specialisation'];
	currentScore?: ProfileFormData['currentScore'];
	gradYear?: ProfileFormData['gradYear'];
	currentScoreType?: ProfileFormData['currentScoreType'];
	selectedDrive?: string;
	driveId?: string;
};
type DriveRegisterUserInput = AdditionalUserDriveRegisteredFormDataType & UserRegisteredFormDataType;
const OfflineDriveUserRegistration = ({
	prefilledPhone,
	prefilledDrive,
	setIsOpenOtpDrawer,
	isOpenOtpDrawer,
	setFormRegisteredDriveData,
	isLoadingVerifyOtp,
	setIsLoadingVerifyOtp,
}: {
	prefilledPhone: string;
	prefilledDrive: { label: string; value: string };
	isOpenOtpDrawer: boolean;
	setIsOpenOtpDrawer: (isOpen: boolean) => void;
	setFormRegisteredDriveData: (data: any) => void;
	isLoadingVerifyOtp: boolean;
	setIsLoadingVerifyOtp: (isLoadingVerifyOtp: boolean) => void;
}) => {
	const toast = useToast();
	const { user } = useUserContext();
	const { themeMode } = useThemeContext();
	const [resume, setResume] = useState<File | null>(null);
	const [selectedDrive, setSelectedDrive] = useState<{ label: string; value: string } | undefined>(undefined);
	const [phone, setPhone] = useState(prefilledPhone);
	const [collegeOptions, setCollegeOptions] = useState<Dropdown | null>(null);
	const [specialisationOptions, setSpecialisationOptions] = useState<Dropdown[]>([]);
	const [userRegisteredData, setUserRegisteredData] = useState<UserRegisteredFormDataType>({
		name: '',
		email: '',
		phone: phone,
		experience: '0',
		usedReferralCode: false,
		companyName: '',
		experienceLevel: '' as UserExperienceLevelType,
	});
	const [additionalUserDriveRegisteredData, setAdditionalUserDriveRegisteredData] =
		useState<AdditionalUserDriveRegisteredFormDataType>({
			techStacks: [],
			gender: 'Male',
			resume: '',
			college: '',
			degree: '',
			specialisation: '',
			currentScore: '',
			gradYear: '',
			currentScoreType: 'percentage' as ScoreType,
			selectedDrive: prefilledDrive?.value,
			driveId: prefilledDrive?.value,
		});
	const isRecruiter = false;
	const [formData, setFormData] = useState<FormDataType>({
		name: '',
		email: '',
		phone: phone,
		experience: '0',
		totalExperience: '',
		companyName: '',
		usedReferralCode: false,
		experienceLevel: '' as UserExperienceLevelType,
		techStacks: [],
		gender: 'Male',
		resume: '',
		college: '',
		degree: '',
		specialisation: '',
		currentScore: '',
		gradYear: '',
		currentScoreType: 'percentage' as ScoreType,
		selectedDrive: prefilledDrive?.value,
	});
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [otpSubmitted, setOtpSubmitted] = useState<boolean>(false);
	const textInputStyle =
		themeMode === 'dark'
			? { color: 'white', backgroundColor: '#4B5563', border: '1px solid #6B7280' }
			: {
					color: 'black',
					backgroundColor: 'white',
					border: '1px solid #D1D5DB',
			  };

	const updateFormData = (
		field: keyof AdditionalUserDriveRegisteredFormDataType,
		value: string | ScoreType | string[] | boolean,
	) => {
		setAdditionalUserDriveRegisteredData(prevData => ({ ...prevData, [field]: value }));
	};

	const { data: skillsData } = useGetAllPublicSkills({
		type: SkillType.SKILL,
	});

	const skillOptions = skillsData?.skills
		?.filter(skill => skill?.skillGroupType)
		?.map(skill => ({ value: skill?._id, label: skill?.name?.replaceAll('_', ' ') }));
	const options = [
		{ label: 'Percentage (100)', value: 'percentage', outOf: 100 },
		{ label: 'GPA (4)', value: 'gpa', outOf: 4 },
		{ label: 'CGPA (10)', value: 'cgpa', outOf: 10 },
	];

	const futureGradYearOptions = gradYearOptions.filter(op => Number(op.value) >= moment().subtract(5, 'year').year());
	const currentScoreOptions = options?.find(
		option => option.value === additionalUserDriveRegisteredData.currentScoreType,
	);
	const collegeName = user?.pifMeta?.underGraduate?.college?.name ?? user?.pifMeta?.postGraduate?.college?.name ?? '';

	const degreeOptions = [
		...Object.keys(UndergraduateDegreeTypes).map(el => ({
			label: el,
			value: UndergraduateDegreeTypes[el as keyof typeof UndergraduateDegreeTypes],
		})),
		...Object.keys(PostgraduateDegreeTypes).map(el => ({
			label: el,
			value: PostgraduateDegreeTypes[el as keyof typeof PostgraduateDegreeTypes],
		})),
	];

	useEffect(() => {
		if (undergraduateDegrees[additionalUserDriveRegisteredData.degree as keyof typeof undergraduateDegrees]) {
			setSpecialisationOptions(
				undergraduateDegrees[additionalUserDriveRegisteredData.degree as keyof typeof undergraduateDegrees]?.map(
					el => ({
						label: el,
						value: el,
					}),
				),
			);
		} else if (postgraduateDegrees[additionalUserDriveRegisteredData.degree as keyof typeof postgraduateDegrees]) {
			setSpecialisationOptions(
				postgraduateDegrees[additionalUserDriveRegisteredData.degree as keyof typeof postgraduateDegrees]?.map(el => ({
					label: el,
					value: el,
				})),
			);
		} else {
			setSpecialisationOptions([]);
		}
	}, [additionalUserDriveRegisteredData.degree]);

	const signupUserFn = useMutation({
		mutationKey: 'registerUser',
		mutationFn: registerUser,
		onSuccess(data) {
			toast({
				type: 'success',
				title: `An OTP has been sent to your registered Phone.`,
			});
			// set userId info into localstorage
			localStorage.setItem('userId', data.userId);
			setIsLoading(false);
			setIsOpenOtpDrawer(true);
			setOtpSubmitted(true);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoadingVerifyOtp(false);
			setOtpSubmitted(false);
		},
	});
	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		let resumeUrl = '';

		if (resume) {
			const fileSizeMB = resume.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'Resume File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			const policy = await getPolicyForS3NonVideoWithoutAuth(
				resume.name?.trim()?.replaceAll(' ', '_'),
				'resumeUrls',
				resume.type,
				true,
			);
			const uploadedUrl = await handleS3Upload(policy, resume);
			if (uploadedUrl) resumeUrl = uploadedUrl;
		}

		const experienceMatch = userRegisteredData.totalExperience?.match(/(\d+)/);
		const totalExperience = experienceMatch ? parseInt(experienceMatch[0], 10) : 0;

		const submissionData = {
			...userRegisteredData,
			...additionalUserDriveRegisteredData,
			experience: userRegisteredData.experience || '0',
			companyName: userRegisteredData.companyName || '',
			usedReferralCode: userRegisteredData.usedReferralCode ? 'true' : 'false',
			experienceLevel: userRegisteredData.experienceLevel || UserExperienceLevelType.FINAL_YEAR_STUDENT,
			resume: resumeUrl,
		};

		console.log('formData', submissionData);
		setFormRegisteredDriveData(submissionData);
		signupUserFn.mutate(submissionData);
		setIsLoadingVerifyOtp(true);
		setIsOpenOtpDrawer(true);
	};
	const experienceOptions = [
		{ label: 'Final Year Student', value: UserExperienceLevelType.FINAL_YEAR_STUDENT, exp: 0 },
		{ label: 'Graduated in 2024', value: UserExperienceLevelType.FRESHER, exp: 0 },
		{ label: '1-2 years experience', value: UserExperienceLevelType.OneToTwoYears, exp: 1 },
		{ label: '2+ years experience', value: UserExperienceLevelType.TwoPlusYears, exp: 2 },
	];
	return (
		<Container className="flex justify-center items-center">
			<div className="max-w-[1480px] w-full mx-auto px-4 lg:px-[80px] [&_input]:bg-white pb-2">
				<form className="max-w-[904px]" onSubmit={handleSubmit}>
					<div className="flex items-end gap-3 pt-[38px]">
						<h2 className="text-xl font-semibold w-fit">Basic details</h2>
					</div>

					<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2 block">
								<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="name" value="Name " />
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								sizing="lg"
								id="name"
								type="text"
								placeholder="Your Name"
								required={true}
								value={userRegisteredData.name}
								onChange={e => setUserRegisteredData({ ...userRegisteredData, name: e.target.value })}
								style={textInputStyle}
							/>
						</div>
						<div className="md:flex-1 w-full">
							<div className="mb-2 block">
								<Label
									className={'dark:text-[#fff] text-[#111928] font-inter'}
									htmlFor="email"
									value={isRecruiter ? 'Work email' : 'Email address'}
								/>
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								sizing="lg"
								id="email"
								type="email"
								pattern={`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`}
								placeholder={isRecruiter ? 'name@company.com' : 'name@provider.com'}
								required={true}
								value={userRegisteredData.email}
								onChange={e => setUserRegisteredData({ ...userRegisteredData, email: e.target.value })}
								style={textInputStyle}
							/>
						</div>
					</div>
					<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2 block">
								<Label
									className={'dark:text-[#fff] text-[#111928] font-inter'}
									htmlFor="selecteddrive"
									value="Selected Drive "
								/>
								<span className="text-red-600">*</span>
							</div>
							<OfflineDriveDropdown
								classNamePrefix="flowbite-select"
								selectedDrive={prefilledDrive}
								setSelectedDrive={setSelectedDrive}
								isDisabled={true}
								closeMenuOnSelect={false}
							/>
						</div>
						<div className="md:flex-1 w-full">
							<div className="mb-2 block">
								<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Phone number " />
								{isRecruiter ? null : <span className="text-red-600">*</span>}
							</div>
							<TextInput
								sizing="lg"
								id="phone"
								type="tel"
								pattern={`^[0-9]{10}$`}
								placeholder="9876543210"
								required={!isRecruiter}
								value={userRegisteredData.phone}
								onChange={e => setUserRegisteredData({ ...userRegisteredData, phone: e.target.value })}
								style={textInputStyle}
								disabled
							/>
						</div>
					</div>
					<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2">
								<Label htmlFor="currentScore" value="Gender" />
								<span className="text-[red]"> *</span>
							</div>

							<div className="flex flex-wrap gap-2">
								<button
									type="button"
									disabled={!!user?.pifMeta?.gender || !!user?.profile?.gender}
									onClick={() => {
										if (user?.pifMeta?.gender || user?.profile?.gender) {
											return;
										}
										updateFormData('gender', 'Male');
									}}
									className={classNames(
										'flex flex-1 !text-center md:flex-[0] justify-center px-[12px] py-[8px] cursor-pointer rounded-lg border w-max disabled:opacity-50',
										additionalUserDriveRegisteredData.gender === 'Male'
											? 'bg-[#18222D] border border-[#7777E7]	text-gray-50'
											: 'bg-white	text-black',
									)}
								>
									Male
								</button>
								<button
									type="button"
									disabled={!!user?.pifMeta?.gender || !!user?.profile?.gender}
									onClick={() => {
										if (user?.pifMeta?.gender || user?.profile?.gender) {
											return;
										}
										updateFormData('gender', 'Female');
									}}
									className={classNames(
										'flex flex-1 !text-center md:flex-[0] justify-center px-[12px] py-[8px] cursor-pointer rounded-lg border w-max disabled:opacity-50',
										additionalUserDriveRegisteredData.gender === 'Female'
											? 'bg-[#18222D] border border-[#7777E7]	text-gray-50'
											: 'bg-white	text-black',
									)}
								>
									Female
								</button>
								<button
									type="button"
									disabled={!!user?.pifMeta?.gender || !!user?.profile?.gender}
									onClick={() => {
										if (user?.pifMeta?.gender || user?.profile?.gender) {
											return;
										}
										updateFormData('gender', 'Other');
									}}
									className={classNames(
										'flex flex-1 !text-center md:flex-[0] justify-center px-[12px] py-[8px] cursor-pointer rounded-lg border w-max disabled:opacity-50',
										additionalUserDriveRegisteredData.gender === 'Other'
											? 'bg-[#18222D] border border-[#7777E7]	text-gray-50'
											: 'bg-white	text-black',
									)}
								>
									Other
								</button>
							</div>
						</div>

						<div className="md:flex-1 w-full md:w-[358px]">
							<div className="mb-2 block">
								<Label
									className={'dark:text-[#fff] text-[#111928] font-inter'}
									htmlFor="experience"
									value="Experience "
								/>
								<span className="text-red-600">*</span>
							</div>

							<div>
								<Select
									id="experience"
									required
									isClearable
									options={experienceOptions}
									placeholder="Select Experience"
									classNamePrefix="flowbite-select"
									value={experienceOptions?.find(item => item.value === userRegisteredData.experienceLevel)}
									onChange={e =>
										setUserRegisteredData({
											...userRegisteredData,
											experience: e?.exp?.toString() || '0',
											experienceLevel: e?.value as UserExperienceLevelType,
										})
									}
								/>
							</div>
						</div>
					</div>

					<div className="flex items-start flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2 block">
								<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="skills" value="Top 3 Skills" />
								<span className="text-red-600">*</span>
							</div>
							<Select
								isMulti
								required
								menuPlacement={'auto'}
								menuPosition="fixed"
								options={skillOptions}
								classNamePrefix="flowbite-select"
								value={skillOptions?.filter(title => formData?.techStacks?.includes(title.value))}
								onChange={(val: MultiValue<Dropdown>) => {
									setFormData(prev => ({ ...prev, techStacks: val.map(item => item.value) }));
								}}
								placeholder="React"
								noOptionsMessage={() => 'No skills found'}
							/>

							{formData?.techStacks?.length && formData?.techStacks?.length < 3 ? (
								<div className="mt-2 text-xs text-red-500">Please choose min of 3 skills</div>
							) : null}
						</div>
						<div className="md:flex-1 w-full">
							<div className="md:flex-1 w-full">
								<div className="mb-2 block">
									<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="resume" value="Resume" />
									<span className="text-red-600"> *</span>
								</div>
								<FileInput
									id="resume"
									sizing="lg"
									disabled={!!(user?.flags?.isMasaiStudent && formData?.resume)}
									accept="application/pdf"
									required={formData?.resume ? false : true}
									onChange={e => {
										if (e.target.files) setResume(e.target.files[0]);
									}}
								/>
								<p className="text-xs text-gray-500 italic mt-3">Only pdf files up to 5mb</p>
							</div>
						</div>
					</div>

					<div className="flex items-end gap-3 pt-[20px] flex-wrap md:flex-nowrap">
						<h2 className="text-xl font-semibold w-fit whitespace-pre">Education details</h2>
						<span className="text-xs text-[#6B7280] font-[400] italic md:whitespace-pre">
							Please note that these are mandatory details required to generate your profile and these cannot be edited
							later apart from ‘Resume’{' '}
						</span>
						<div className="h-full border-t-[1px] border-[#C7C9D9] flex-1" />
					</div>

					<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[428px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2">
								<Label htmlFor="collegeName" value="University Name/ College Name" />
								<span className="text-[red]"> *</span>
							</div>

							<CollegeSelect
								disabled={
									!!user?.pifMeta?.underGraduate?.collegeUniversityName ||
									!!user?.pifMeta?.postGraduate?.collegeUniversityName
								}
								value={
									additionalUserDriveRegisteredData?.college && collegeName
										? { label: collegeName, value: collegeName }
										: collegeOptions
										  ? collegeOptions
										  : undefined
								}
								onChange={value => {
									if (
										user?.pifMeta?.underGraduate?.collegeUniversityName ||
										user?.pifMeta?.postGraduate?.collegeUniversityName
									) {
										return;
									}
									if (value) {
										if (Array.isArray(value)) {
											return;
										} else {
											setCollegeOptions(value);
											updateFormData('college', value.value);
										}
									} else {
										setCollegeOptions(null);
										updateFormData('college', '');
									}
								}}
							/>
						</div>
					</div>

					<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2">
								<Label htmlFor="degree" value="UG/PG Degree" />
								<span className="text-[#555770] text-xs"> (Whichever is latest)</span>
								<span className="text-[red]"> *</span>
							</div>

							<Select
								required
								id="degree"
								isClearable
								classNamePrefix="flowbite-select"
								isDisabled={!!user?.pifMeta?.underGraduate?.degree || !!user?.pifMeta?.postGraduate?.degree}
								value={degreeOptions?.find(item => item.value === additionalUserDriveRegisteredData.degree)}
								onChange={(value: SingleValue<Dropdown>) => {
									if (user?.pifMeta?.underGraduate?.degree || user?.pifMeta?.postGraduate?.degree) {
										return;
									}
									updateFormData('degree', value?.value ?? '');
									updateFormData('specialisation', '');
								}}
								options={degreeOptions}
							/>
						</div>
						<div className="md:flex-1 w-full">
							<div className="mb-2">
								<Label htmlFor="specialisation" value="UG/PG Specialisation" />
								<span className="text-[#555770] text-xs"> (Whichever is latest)</span>
								<span className="text-[red]"> *</span>
							</div>

							<Select
								required
								isClearable
								id="specialisation"
								isDisabled={
									!!user?.pifMeta?.underGraduate?.specialisation || !!user?.pifMeta?.postGraduate?.specialisation
								}
								options={specialisationOptions}
								value={
									additionalUserDriveRegisteredData.specialisation
										? specialisationOptions?.find(
												item => item.value === additionalUserDriveRegisteredData.specialisation,
										  )
										: null
								}
								onChange={(value: SingleValue<Dropdown>) => {
									if (user?.pifMeta?.underGraduate?.specialisation || user?.pifMeta?.postGraduate?.specialisation) {
										return;
									}
									updateFormData('specialisation', value?.value ?? '');
								}}
								classNamePrefix="flowbite-select"
							/>
						</div>
					</div>

					<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
						<div className="md:flex-1 w-full">
							<div className="mb-2">
								<Label
									htmlFor="currentScore"
									value={
										user?.userPreference?.experienceLevel === UserExperienceLevelType.FINAL_YEAR_STUDENT
											? 'Current GPA'
											: 'Current Score'
									}
								/>
								<span className="text-[red]"> *</span>
							</div>
							<TextInput
								color="purple"
								id="currentScore"
								type="number"
								className="RemoveInputArrow [&_input]:pr-[193px]"
								sizing="lg"
								min={0}
								step="0.01"
								disabled={!!user?.pifMeta?.underGraduate?.percentage || !!user?.pifMeta?.postGraduate?.percentage}
								max={currentScoreOptions?.outOf}
								value={additionalUserDriveRegisteredData.currentScore}
								onChange={e => {
									if (user?.pifMeta?.underGraduate?.percentage || user?.pifMeta?.postGraduate?.percentage) {
										return;
									}
									if (
										(currentScoreOptions && Number(e.target.value) > currentScoreOptions?.outOf) ||
										Number(e.target.value) < 0
									) {
										return;
									}
									updateFormData('currentScore', e.target.value);
								}}
								required
								placeholder={`Out of ${currentScoreOptions?.outOf}`}
								rightIcon={() => {
									return (
										<div className="pointer-events-auto border-l-[2px] -mr-3 border-[#D1D5DB] h-full flex items-center px-4 relative min-w-[194px] justify-between">
											<CustomDropDown
												buttonClassName="justify-between"
												label={currentScoreOptions?.label || ''}
												disabled={!!user?.pifMeta?.underGraduate?.scoreType || !!user?.pifMeta?.postGraduate?.scoreType}
												buttonStyle={{
													color: '#6B7280',
												}}
												options={options}
												onChange={value => {
													if (user?.pifMeta?.underGraduate?.scoreType || user?.pifMeta?.postGraduate?.scoreType) {
														return;
													}
													updateFormData('currentScoreType', value as ScoreType);
													updateFormData('currentScore', '');
												}}
											/>
										</div>
									);
								}}
							/>
						</div>
						<div className="md:flex-1 w-full">
							<div className="mb-2">
								<Label htmlFor="gradYear" value="Graduation Year" />
								<span className="text-[red]"> *</span>
							</div>

							<Select
								id="gradYear"
								required
								isClearable
								menuPlacement="auto"
								options={futureGradYearOptions}
								classNamePrefix="flowbite-select"
								isDisabled={!!user?.profile?.gradYear}
								value={futureGradYearOptions?.find(
									item => Number(item.value) === Number(additionalUserDriveRegisteredData.gradYear),
								)}
								onChange={value => {
									if (user?.profile?.gradYear) {
										return;
									}
									updateFormData('gradYear', value?.value ?? '');
								}}
							/>
						</div>
					</div>

					<div className="flex items-center gap-2 mt-[43px]">
						<Button
							size="lg"
							type="submit"
							color="purplePlaced"
							className="font-inter w-full max-w-[167px]"
							disabled={signupUserFn.isLoading}
							isProcessing={signupUserFn.isLoading}
						>
							Next <HiOutlineArrowRight className="ml-2" />
						</Button>
					</div>
				</form>
			</div>
		</Container>
	);
};

export default OfflineDriveUserRegistration;
